import React, { lazy } from 'react';
import { motion } from 'framer-motion';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
interface ReasonSectionProps {
  number: number;
  objection: string;
  title: string;
  children: React.ReactNode;
}
function ReasonSection({
  number,
  objection,
  title,
  children
}: ReasonSectionProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6
  }} className="mt-9" data-id="element-2">
      <div className="mb-2" data-id="element-3">
        <span className="font-sans-editorial inline-flex items-center justify-center bg-[#2f7d5b] text-white text-[13px] font-extrabold tracking-[1px] w-[34px] h-[34px] rounded-full mb-[11px]" data-id="element-4">
          {number}
        </span>
        <div className="font-sans-editorial text-[13px] font-bold italic text-[#6b6b62] mb-[6px]" data-id="element-5">
          "{objection}"
        </div>
        <h2 className="font-serif-editorial text-[24px] md:text-[26px] font-bold leading-[1.28] text-[#1a1a17] tracking-tight mb-4" data-id="element-6">
          {title}
        </h2>
      </div>
      <div className="space-y-[18px] text-[#3d3d38]" data-id="element-7">{children}</div>
    </motion.div>;
}
interface ReviewProps {
  quote: string;
  name: string;
}
function Review({
  quote,
  name
}: ReviewProps) {
  return <div className="bg-[#faf8f4] border border-[#e6e2d9] rounded-[10px] p-4 md:p-[18px] mb-[14px]" data-id="element-8">
      <div className="text-[#e8a93b] text-[15px] tracking-[2px] mb-2" data-id="element-9">
        ★★★★★
      </div>
      <div className="italic text-[16.5px] text-[#1a1a17] leading-[1.55] mb-[9px]" data-id="element-10">
        "{quote}"
      </div>
      <div className="font-sans-editorial text-[13px] font-bold text-[#6b6b62]" data-id="element-11">
        {name}
      </div>
    </div>;
}
interface PlantProps {
  name: string;
  description: React.ReactNode;
  imageSrc?: string;
  isKey?: boolean;
  titleColor?: string;
}
function Plant({
  name,
  description,
  imageSrc,
  isKey,
  titleColor = '#1a1a17'
}: PlantProps) {
  if (imageSrc) {
    return <div className="text-left" data-id="element-12">
        <img src={imageSrc} alt={name} className="w-[104px] h-[104px] object-contain block mb-2 mix-blend-multiply" data-id="element-13" />
        {isKey && <span className="inline-block font-sans-editorial text-[10px] font-extrabold tracking-[0.5px] uppercase text-white bg-[#2f7d5b] px-2 py-0.5 rounded-full mb-1.5" data-id="element-14">
            La planta clave
          </span>}
        <div className="font-sans-editorial font-extrabold text-[16px] mb-1 leading-[1.2]" style={{
        color: titleColor
      }} data-id="element-15">
          {name}
        </div>
        <div className="text-[14.5px] leading-[1.45] text-[#3d3d38]" data-id="element-16">
          {description}
        </div>
      </div>;
  }
  return <div className="mb-3 last:mb-0 text-[16px] leading-[1.5]" data-id="element-17">
      <span className="text-[17px] mr-1" data-id="element-18">🌿</span>
      <b className="text-[#0f3f46]" data-id="element-19">{name}</b> — {description}
    </div>;
}
export function AdvertorialPage() {
  return <div className="bg-[#faf8f4] min-h-screen pb-24" data-id="element-20">
      <div className="max-w-[600px] mx-auto bg-white min-h-screen shadow-[0_0_60px_rgba(0,0,0,0.04)]" data-id="element-21">
        {/* Masthead */}
        <div className="border-b border-[#e6e2d9] px-[22px] py-[14px] flex items-center justify-between bg-white" data-id="element-22">
          <div className="flex items-center gap-[9px]" data-id="element-23">
            <div className="w-[30px] h-[30px] rounded-full bg-gradient-to-br from-[#2f8f7a] to-[#1f6e8c] flex items-center justify-center text-white font-sans-editorial font-extrabold text-[15px]" data-id="element-24">
              S
            </div>
            <div className="font-sans-editorial font-extrabold text-[17px] tracking-tight text-[#1a1a17]" data-id="element-25">
              Salud
              <span className="text-[#6b6b62] font-medium" data-id="element-26">yBienestar</span>
            </div>
          </div>
          <div className="font-sans-editorial text-[11px] font-bold tracking-[1.5px] uppercase text-[#2f7d5b]" data-id="element-27">
            Salud · Bienestar
          </div>
        </div>

        <article className="px-5 pt-[26px] text-[19.5px] leading-[1.6]" data-id="element-28">
          {/* Kicker */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="font-sans-editorial text-[12px] font-bold tracking-[1.2px] uppercase text-[#2f7d5b] mb-3" data-id="element-29">
            Informe · Salud y bienestar
          </motion.div>

          {/* Headline */}
          <motion.h1 initial="hidden" animate="visible" variants={fadeIn} transition={{
          delay: 0.05
        }} className="font-serif-editorial text-[32px] md:text-[36px] font-bold leading-[1.15] tracking-[-0.3px] text-[#1a1a17] mb-5" data-id="element-30">
            5 Razones Por Las Que Esta Potente Fórmula Natural Está Cambiando
            Cómo Combatimos La Hinchazón De Pies
          </motion.h1>

          {/* Hero GIF */}
          <motion.figure initial="hidden" animate="visible" variants={fadeIn} transition={{
          delay: 0.12
        }} className="mt-1.5 mb-[22px] rounded-xl overflow-hidden bg-[#eceae4] leading-none" data-id="element-31">
            <img src="https://cdn.shopify.com/s/files/1/1003/8303/1638/files/0530_2.gif?v=1780150110" alt="Persona sentada en el sofá con molestias en las piernas y tobillos hinchados" className="w-full h-auto block" loading="eager" data-id="element-32" />
          </motion.figure>

          {/* Byline */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} transition={{
          delay: 0.15
        }} className="flex gap-[13px] items-center py-[15px] border-y border-[#e6e2d9] mb-[26px]" data-id="element-33">
            <div className="w-[46px] h-[46px] rounded-full flex-shrink-0 bg-[#cdd8d8]" style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 46 46'%3E%3Crect width='46' height='46' fill='%23cdd8d8'/%3E%3Ccircle cx='23' cy='18' r='8' fill='%23a9bcbc'/%3E%3Cpath d='M9 44c0-9 6-14 14-14s14 5 14 14' fill='%23a9bcbc'/%3E%3C/svg%3E\")",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} aria-hidden="true" data-id="element-34" />
            <div className="font-sans-editorial text-[13.5px] leading-[1.45]" data-id="element-35">
              <b className="block font-bold text-[15px] text-[#1a1a17]" data-id="element-36">
                Por la Dra. Elena Ruiz
              </b>
              <span className="text-[#6b6b62]" data-id="element-37">
                Flebóloga · Madrid · Miembro del Capítulo Español de Flebología
                y Linfología · 30 de mayo de 2026
              </span>
            </div>
          </motion.div>

          {/* Body intro */}
          <div className="space-y-[18px] text-[#3d3d38]" data-id="element-38">
            <p className="text-[20px] text-[#1a1a17] font-medium" data-id="element-39">
              Cuando no hay hinchazón, apenas pensamos en nuestras piernas.
            </p>
            <p data-id="element-40">
              Nos llevan a todas partes, aguantan la jornada de pie, nos
              acompañan en cada paseo. Pero cuando la retención de líquidos
              aparece, lo cambia todo.
            </p>
            <p data-id="element-41">La realidad es esta:</p>
          </div>

          {/* Truth list */}
          <ul className="bg-[#f4f1ea] rounded-[10px] p-[18px] md:px-5 my-[22px] mt-1 list-none" data-id="element-42">
            {['Estar muchas horas de pie deja las piernas pesadas y cansadas', 'A partir de los 45, el cuerpo retiene cada vez más líquido sin que cambies nada', 'Por la tarde los zapatos aprietan, los tobillos desaparecen y las marcas del calcetín no se van', 'Y hasta el calzado de verano te empieza a apretar'].map((item, i) => <li key={i} className="relative pl-6 mb-[11px] last:mb-0 text-[16.5px] leading-[1.5] text-[#3d3d38]" data-id="element-43">
                <span className="absolute left-0 top-[9px] w-[9px] h-[9px] rounded-full bg-[#2f7d5b]" data-id="element-44" />
                {item}
              </li>)}
          </ul>

          <p className="text-[#3d3d38] mb-[18px]" data-id="element-45">
            Si estás cansado de diuréticos que te dejan agotado, de drenajes
            manuales que cuestan una fortuna y de medias que no solucionan nada,
            hay otra vía. Y cabe en un cajón.
          </p>

          {/* Bridge */}
          <p className="font-serif-editorial text-[19px] leading-[1.55] text-[#1a1a17] font-medium my-[22px] mb-1" data-id="element-46">
            Y no,{' '}
            <b className="text-[#9c2a2a]" data-id="element-47">
              no es otro diurético disfrazado de natural.
            </b>{' '}
            Estas son las 5 razones por las que tanta gente está dejando todo lo
            demás por esta fórmula:
          </p>

          {/* Reason 1 */}
          <ReasonSection number={1} objection="¿En qué se diferencia esto de un diurético?" title="Alivia La Hinchazón Sin Fármacos" data-id="element-48">
            <figure className="my-2 rounded-xl overflow-hidden border border-[#e6e2d9] leading-none" data-id="element-49">
              <img src="https://cdn.magicpatterns.com/uploads/muqLVJ3m7X8x6ouqmqvyJp/ChatGPT_Image_30_may_2026,_16_08_11.png" alt="Comparativa: pastillas vs Drenaje Linfático Sonvital en cuchara" className="w-full h-auto block" loading="lazy" data-id="element-50" />
            </figure>
            <p data-id="element-51">
              Imagina llegar al final del día y poder quitarte los zapatos sin
              esa sensación de tener los pies y los tobillos hinchados.
            </p>
            <p data-id="element-52">
              Para eso se creó{' '}
              <strong className="text-[#1a1a17]" data-id="element-53">
                Drenaje Linfático de Sonvital
              </strong>
              , y su secreto tiene nombre: la{' '}
              <strong className="text-[#1a1a17]" data-id="element-54">Raíz de Stillingia</strong>.
            </p>
            <p data-id="element-55">
              Mientras casi todos los drenantes del mercado intentan empujar el
              líquido hacia fuera, la Stillingia hace algo distinto: se orienta
              a los bloqueos más profundos del sistema linfático, los que las
              medias y los diuréticos nunca alcanzan. Es decir, abre la salida
              por donde el líquido tiene que irse. Sin eso, todo lo demás es
              trabajo perdido.<sup data-id="element-56">*</sup>
            </p>
            <p data-id="element-57">
              Pero la Stillingia sola no basta, y por eso Sonvital la combina en
              una <strong className="text-[#1a1a17]" data-id="element-58">Fórmula 4 en 1</strong> de
              plantas que trabajan en secuencia:
            </p>
            <div className="bg-[#faf8f4] border border-[#e6e2d9] rounded-xl p-5 md:p-6 my-2 grid grid-cols-2 gap-x-[18px] gap-y-[26px]" data-id="element-59">
              <Plant name="Raíz de Stillingia" description="Abre los bloqueos linfáticos más profundos, los que los demás no alcanzan." imageSrc="https://cdn.magicpatterns.com/uploads/fZra5PbW8ZiVCdKT9Qs9gT/32.png" isKey={true} titleColor="#195A63" data-id="element-60" />
              <Plant name="Amor de Hortelano" description='La "escoba linfática": despierta los vasos linfáticos dormidos.' imageSrc="https://cdn.magicpatterns.com/uploads/ni41kiZBnWm112jfoW4XMb/31.png" data-id="element-61" />
              <Plant name="Corteza de Fresno Espinoso" description="Reactiva la microcirculación final por donde sale el líquido." imageSrc="https://cdn.magicpatterns.com/uploads/aBjBAhcgNZt6f1bK7PKtCe/30.png" data-id="element-62" />
              <Plant name="Trébol Rojo" description="Acompaña el equilibrio hormonal que también influye en la retención." imageSrc="https://cdn.magicpatterns.com/uploads/qrddJ32654E8C8yLccbrJA/29.png" data-id="element-63" />
            </div>
            <p data-id="element-64">
              Y lo mejor: a diferencia de los diuréticos, que te dejan agotado y
              con efecto rebote en cuanto los dejas, esta es una fórmula 100%
              botánica en extracto líquido, pensada para tomar a diario sin que
              el cuerpo se acostumbre.<sup data-id="element-65">*</sup>
            </p>
            <p data-id="element-66">
              Con el uso constante, esa pesadez y esa hinchazón dejan de ser tu
              normalidad de cada tarde.<sup data-id="element-67">*</sup>
            </p>
          </ReasonSection>

          {/* Reason 2 */}
          <ReasonSection number={2} objection="No tengo tiempo para rutinas complicadas" title="Se toma en diez segundos por la mañana y ya está" data-id="element-68">
            <p data-id="element-69">
              <strong className="text-[#1a1a17]" data-id="element-70">
                ¼ de cucharadita al día
              </strong>{' '}
              disuelta en agua, café o zumo. Cada bote trae su cucharilla
              dosificadora. Sin pastillas que tragar, sin reservar cita para un
              drenaje, sin geles que aplicar dos veces al día.
            </p>
            <p data-id="element-71">
              Es el tipo de gesto que encaja en cualquier mañana — y que, día
              tras día, va sumando sin que tengas que pensar en él. Mucha gente
              lo deja al lado de la cafetera para no olvidarlo.
            </p>
          </ReasonSection>

          {/* Reason 3 */}
          <ReasonSection number={3} objection="¿Y si mi hinchazón es simplemente por la edad?" title="Está pensado precisamente para la retención que se dispara a partir de los 45" data-id="element-72">
            <p data-id="element-73">
              Si la hinchazón se te ha disparado a partir de los 45-50, no es
              casualidad. El sistema linfático se vuelve más lento con la edad y
              le cuesta cada vez más drenar el líquido, y a eso súmale el calor
              mediterráneo medio año. La tormenta perfecta.
            </p>
            <p data-id="element-74">
              Por eso la fórmula combina varias plantas que trabajan sobre ese
              proceso, incluido el{' '}
              <strong className="text-[#1a1a17]" data-id="element-75">Trébol Rojo</strong>, que ayuda
              a equilibrar los desajustes hormonales que también influyen en la
              retención a partir de cierta edad.
              <sup data-id="element-76">*</sup> Es el detalle que hace que tanta gente diga que es lo
              primero que "le ha encajado de verdad".
            </p>
          </ReasonSection>

          {/* Reason 4 — with embedded reviews */}
          <ReasonSection number={4} objection="¿Tengo alguna prueba de que esto funciona?" title="Más de 13.000 personas en España ya lo usan a diario" data-id="element-77">
            <p data-id="element-78">
              No hace falta creer en promesas: basta con escuchar a quienes ya
              lo toman. Más de{' '}
              <strong className="text-[#1a1a17]" data-id="element-79">13.000 personas</strong> en
              España lo han incorporado a su rutina, y los comentarios se
              repiten tanto que cuesta ignorarlos.
            </p>
            <div className="my-[30px]" data-id="element-80">
              <Review quote="Trabajo de pie todo el día y llegaba a casa con las piernas como troncos. Llevo un mes con esto y la diferencia por la tarde se nota muchísimo." name="Antonio R., 57 — Sevilla" data-id="element-81" />
              <Review quote="Tres semanas y las piernas ni las reconozco. Yo era de subir los pies al sofá sí o sí. Ahora aguanto el día entero." name="Trini M., 51 — Valencia" data-id="element-82" />
              <Review quote="Probé de todo, diuréticos incluidos, y nada me duraba. Esto fue lo primero que fue a por la causa y no solo a quitarme el agua de encima." name="Miguel A., 60 — Madrid" data-id="element-83" />
            </div>
          </ReasonSection>

          {/* Reason 5 */}
          <ReasonSection number={5} objection="¿Y si pago y a mí no me funciona?" title="60 días para probarlo, con devolución completa si no notas la diferencia" data-id="element-84">
            <p data-id="element-85">
              Cada pedido viene con una{' '}
              <strong className="text-[#1a1a17]" data-id="element-86">
                garantía de devolución de 60 días
              </strong>
              . Si no notas la diferencia, devuelves el frasco —aunque esté
              vacío— y te devuelven el dinero. Sin preguntas.
            </p>
            <p data-id="element-87">
              Es la forma de que el riesgo lo asuma la marca, no tú: si no
              funcionara, no podrían permitirse ofrecer dos meses enteros de
              garantía.
            </p>
          </ReasonSection>

          {/* En resumen — versus box */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="bg-[#195A63] rounded-xl p-5 my-[22px] text-[#eaf3f3]" data-id="element-88">
            <div className="font-sans-editorial font-extrabold text-[13px] tracking-[1px] uppercase text-[#a9cdd0] mb-[14px] text-center" data-id="element-89">
              En resumen
            </div>
            <div className="flex gap-3 items-start mb-[11px] text-[15.5px] leading-[1.45]" data-id="element-90">
              <span className="text-[#ff9b9b] font-extrabold flex-shrink-0" data-id="element-91">
                ✕
              </span>
              <div data-id="element-92">
                Los demás atacan <b className="text-white" data-id="element-93">una sola cosa</b> — o
                son diuréticos, o cremas, o medias — y el alivio dura lo que
                dura su uso
              </div>
            </div>
            <div className="flex gap-3 items-start text-[15.5px] leading-[1.45]" data-id="element-94">
              <span className="text-[#86e0a8] font-extrabold flex-shrink-0" data-id="element-95">
                ✓
              </span>
              <div data-id="element-96">
                Sonvital combina{' '}
                <b className="text-white" data-id="element-97">4 plantas en secuencia</b> y va a la
                causa, en <b className="text-white" data-id="element-98">extracto líquido</b> que se
                absorbe mucho antes que una cápsula
              </div>
            </div>
          </motion.div>

          {/* Alert — Señales de que tu sistema linfático necesita ayuda */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="bg-[#fdf3f1] border border-[#f0d4cc] border-l-4 border-l-[#9c2a2a] rounded-r-[10px] p-5 my-[30px]" data-id="element-99">
            <h3 className="font-sans-editorial font-extrabold text-[16px] text-[#9c2a2a] mb-[13px] leading-[1.35]" data-id="element-100">
              Señales de que tu sistema linfático necesita ayuda ya
            </h3>
            <ul className="list-none" data-id="element-101">
              {['Te quitas el calcetín y la marca tarda en desaparecer', 'El mismo zapato te entra por la mañana y aprieta por la tarde', 'Te cuesta sacarte el anillo al final del día', 'Duermes 8 horas y te levantas igual de hinchado', 'Notas las piernas más cansadas y pesadas que hace un año'].map((item, i) => <li key={i} className="relative pl-[26px] mb-[10px] last:mb-0 text-[16px] leading-[1.5] text-[#3d3d38]" data-id="element-102">
                  <span className="absolute left-0 top-0 text-[#9c2a2a] text-[14px]" data-id="element-103">
                    ⚠
                  </span>
                  {item}
                </li>)}
            </ul>
            <p className="mt-[14px] mb-0 text-[16px] text-[#1a1a17] font-medium" data-id="element-104">
              Si te reconoces en tres o más, la retención ya no es puntual: es
              una costumbre que tu cuerpo está adoptando. Y cada temporada que
              pasa cuesta un poco más revertirla.
            </p>
          </motion.div>

          {/* Stock Bar */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="bg-[#fff6e8] border border-dashed border-[#F5A623] rounded-[10px] px-4 py-[13px] mt-6 mb-2 font-sans-editorial font-bold text-[14.5px] text-[#8a5a06] text-center leading-[1.5]" data-id="element-105">
            ⚠️ STOCK LIMITADO — Últimos packs disponibles ·{' '}
            <span className="text-[#9c2a2a]" data-id="element-106">
              50% DESCUENTO + envío gratis 24-48h
            </span>
          </motion.div>

          {/* Product Box */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="rounded-2xl p-6 md:p-[26px] mt-[18px] mb-[30px] text-center text-[#eaf3f3]" style={{
          background: 'linear-gradient(160deg, #195A63, #0f3f46)'
        }} data-id="element-107">
            <div className="font-sans-editorial font-extrabold text-[20px] tracking-[0.3px] text-white mb-1" data-id="element-108">
              SON<i className="not-italic text-[#F5A623]" data-id="element-109">VITAL</i>
            </div>
            <div className="font-sans-editorial text-[12.5px] tracking-[2px] uppercase text-[#a9cdd0] mb-[18px]" data-id="element-110">
              Drenaje Linfático · Fórmula 4 en 1
            </div>
            <div className="font-sans-editorial font-extrabold text-[26px] text-white mb-2" data-id="element-111">
              Oferta de lanzamiento
            </div>
            <div className="inline-block bg-[#F5A623] text-[#3a2600] font-sans-editorial font-extrabold text-[13px] px-3 py-[5px] rounded-full mb-4" data-id="element-112">
              −50% HOY
            </div>
            <div className="font-sans-editorial text-[13.5px] text-[#cfe4e5] mb-5" data-id="element-113">
              Envío GRATIS en 24-48h a toda España · Garantía 60 días
            </div>
            <a href="#comprar" className="cta-button block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[18px] no-underline text-center px-[18px] py-[17px] rounded-xl tracking-[0.2px] leading-[1.3] hover:brightness-110" data-id="element-114">
              Ver Si Queda Stock →<br data-id="element-115" />
              Con 50% De Descuento
            </a>
          </motion.div>

          {/* Guarantee */}
          <div className="flex gap-[14px] items-center bg-[#faf8f4] border border-[#e6e2d9] rounded-xl p-4 md:px-[18px] md:py-4 my-[22px]" data-id="element-116">
            <div className="flex-shrink-0 w-[54px] h-[54px] rounded-full bg-[#1f9d55] text-white flex items-center justify-center font-sans-editorial font-extrabold text-[13px] text-center leading-[1.1]" data-id="element-117">
              60
              <br data-id="element-118" />
              DÍAS
            </div>
            <div className="text-[15px] leading-[1.5] text-[#3d3d38]" data-id="element-119">
              <b className="text-[#1a1a17]" data-id="element-120">
                Garantía de devolución de 60 días.
              </b>{' '}
              Si no notas la diferencia, devuelves el frasco (aunque esté vacío)
              y te devuelven el dinero. Sin preguntas.
            </div>
          </div>

          <p className="text-center font-sans-editorial text-[14px] text-[#6b6b62] mt-[10px] mb-0" data-id="element-121">
            Más de <b className="text-[#1a1a17]" data-id="element-122">13.000 personas en España</b>{' '}
            ya lo han incorporado a su rutina diaria.
          </p>

          {/* Solo CTA */}
          <a href="#comprar" className="cta-button block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[18px] no-underline text-center px-[18px] py-[17px] rounded-xl tracking-[0.2px] leading-[1.3] my-[26px] hover:brightness-110" data-id="element-123">
            Ver Si Queda Stock Con 50% De Descuento
          </a>
        </article>

        {/* Footer */}
        <footer className="border-t border-[#e6e2d9] mt-[34px] px-[22px] pt-6 pb-[120px] font-sans-editorial text-[11.5px] text-[#6b6b62] leading-[1.6]" data-id="element-124">
          <div className="mb-[14px]" data-id="element-125">
            <sup data-id="element-126">*</sup> Complemento alimenticio. No sustituye una dieta variada
            y equilibrada ni un estilo de vida saludable. No superar la dosis
            diaria recomendada. Mantener fuera del alcance de los niños. Si
            estás embarazada, en período de lactancia, tomas medicación o tienes
            alguna condición médica, consulta con tu profesional sanitario antes
            de tomarlo.
          </div>
          <div data-id="element-127">
            © 2026 Salud y Bienestar. Todos los derechos reservados. ·{' '}
            <a href="#" className="text-[#6b6b62] hover:underline" data-id="element-128">
              Privacidad
            </a>{' '}
            ·{' '}
            <a href="#" className="text-[#6b6b62] hover:underline" data-id="element-129">
              Términos
            </a>{' '}
            ·{' '}
            <a href="#" className="text-[#6b6b62] hover:underline" data-id="element-130">
              Contacto
            </a>
          </div>
          <div className="mt-[10px]" data-id="element-131">
            Este sitio no forma parte del sitio web de Facebook ni de Facebook
            Inc. Tampoco está respaldado por Facebook de ninguna manera.
            FACEBOOK es una marca registrada de Facebook, Inc.
          </div>
        </footer>
      </div>

      {/* Sticky CTA */}
      <div className="fixed left-0 right-0 bottom-0 z-50 bg-white/95 backdrop-blur-md border-t border-[#e6e2d9] py-[11px] px-4 shadow-[0_-6px_24px_rgba(0,0,0,0.07)]" data-id="element-132">
        <div className="max-w-[600px] mx-auto" data-id="element-133">
          <a href="#comprar" className="cta-sticky block bg-[#1f9d55] text-white font-sans-editorial font-extrabold text-[16px] no-underline text-center py-[14px] rounded-[11px] hover:brightness-110 transition-all" data-id="element-134">
            Ver Si Queda Stock — 50% DTO
          </a>
          <div className="font-sans-editorial text-[11px] text-[#6b6b62] text-center mt-[5px]" data-id="element-135">
            Envío gratis 24-48h · Garantía 60 días
          </div>
        </div>
      </div>
    </div>;
}